import { ShaderProgram } from "./shader_program.js";

interface ICachedShader
{
  program: ShaderProgram;
  ref_count: number;
}

export class ShaderCache
{
  private static readonly context_caches: WeakMap<WebGL2RenderingContext, Map<number, ICachedShader>> = new WeakMap();

  private static getContextCache( gl: WebGL2RenderingContext ): Map<number, ICachedShader>
  {
    let cache: Map<number, ICachedShader> | undefined = this.context_caches.get( gl );
    if ( !cache )
    {
      cache = new Map<number, ICachedShader>();
      this.context_caches.set( gl, cache );
    }
    return cache;
  }

  /** 
   * Швидка хеш-функція (cyrb53) для генерації унікального ID на основі GLSL коду.
   * Оскільки GLSL рядки можуть бути довгими (10-50КБ), ми їх хешуємо,
   * щоб не тримати копії великих рядків як ключі в Map.
   */
  private static hash( str: string ): number
  {
    let h1: number = 0xdeadbeef;
    let h2: number = 0x41c6ce57;
    for ( let i = 0; i < str.length; ++i )
    {
      const ch: number = str.charCodeAt( i );
      h1 = Math.imul( h1 ^ ch, 2654435761 );
      h2 = Math.imul( h2 ^ ch, 1597334677 );
    }
    h1 = Math.imul( h1 ^ ( h1 >>> 16 ), 2246822507 ) ^ Math.imul( h2 ^ ( h2 >>> 13 ), 3266489909 );
    h2 = Math.imul( h2 ^ ( h2 >>> 16 ), 2246822507 ) ^ Math.imul( h1 ^ ( h1 >>> 13 ), 3266489909 );
    return 4294967296 * ( 2097151 & h2 ) + ( h1 >>> 0 );
  }

  public static getProgram( gl: WebGL2RenderingContext, vertex_source: string, fragment_source: string ): ShaderProgram
  {
    const cache: Map<number, ICachedShader> = this.getContextCache( gl );
    const key: number = this.hash( vertex_source + "|||" + fragment_source );
    const cached: ICachedShader | undefined = cache.get( key );

    if ( cached )
    {
      cached.ref_count++;
      return cached.program;
    }

    const program: ShaderProgram = new ShaderProgram( gl, vertex_source, fragment_source );
    cache.set( key, { program, ref_count: 1 } );
    return program;
  }

  public static releaseProgram( gl: WebGL2RenderingContext, program: ShaderProgram ): void
  {
    const cache: Map<number, ICachedShader> = this.getContextCache( gl );
    for ( const [key, cached] of cache.entries() )
    {
      if ( cached.program === program )
      {
        cached.ref_count--;
        if ( cached.ref_count <= 0 )
        {
          program.dispose( gl );
          cache.delete( key );
        }
        break;
      }
    }
  }

  public static clear( gl: WebGL2RenderingContext ): void
  {
    const cache: Map<number, ICachedShader> | undefined = this.context_caches.get( gl );
    if ( cache )
    {
      for ( const cached of cache.values() )
        cached.program.dispose( gl );

      cache.clear();
    }
  }
}
